/**
 * useSystemStatus — tiny app-wide poll of /api/status.
 *
 * Design goals:
 *  - Single fetch per app instance (module-level state + listeners).
 *  - Polls every 60 s (public data — no need to burst).
 *  - Failing/missing status is treated as "unknown" (not "outage") so a
 *    transient network blip on the client never displays a false red banner.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

let cached = null;              // last successful payload
let inflight = null;            // dedupe concurrent fetches
let listeners = new Set();
let interval = null;

const notify = () => listeners.forEach((fn) => fn(cached));

const fetchOnce = async () => {
    if (inflight) return inflight;
    inflight = axios
        .get(`${API}/status`, { timeout: 5000 })
        .then((r) => {
            cached = r.data;
            notify();
            return r.data;
        })
        .catch(() => {
            // keep prior cache on failure — never flap the UI to "outage"
            // just because a client network blip happened.
            return cached;
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
};

const ensurePoll = () => {
    if (interval) return;
    fetchOnce();
    interval = setInterval(fetchOnce, POLL_MS);
};

const teardown = () => {
    if (listeners.size === 0 && interval) {
        clearInterval(interval);
        interval = null;
    }
};

export function useSystemStatus() {
    const [state, setState] = useState(cached);

    useEffect(() => {
        listeners.add(setState);
        ensurePoll();
        return () => {
            listeners.delete(setState);
            teardown();
        };
    }, []);

    return state; // null | { overall, components, generated_at }
}

/**
 * Reduce the `overall` string to a simple UI tone.
 *  - "operational" | "not_configured" → "ok"
 *  - "degraded"                       → "warn"
 *  - "outage"                         → "bad"
 *  - null / unknown                   → "unknown"
 */
export function statusTone(overall) {
    if (overall === "operational" || overall === "not_configured") return "ok";
    if (overall === "degraded") return "warn";
    if (overall === "outage") return "bad";
    return "unknown";
}
